import { useState } from 'react';
import { LanguageCode } from 'i18n/internationalization.types';
import useSystemTemplatePreviewMutation from 'components/domain/automation/systemTemplates/useSystemTemplatePreviewMutation';
import RichTextEditorField from 'components/forms/richTextEditor/RichTextEditorField';
import SelectField from 'components/forms/selectField/SelectField';
import TextField from 'components/forms/textField/TextField';
import { MessagingType } from 'models/automation/CustomTemplates';
import { UsageType } from 'models/automation/Common';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TemplateModalLanguageSelectField from 'pages/automations/common/TemplateModalLanguageSelectField';
import { LanguageFieldsContainer } from 'pages/automations/common/invalidLanguageAlert/LanguageFieldsContainer.styled';
import InvalidLanguageAlert from 'pages/automations/common/invalidLanguageAlert/InvalidLanguageAlert';
import { Col, FormControl } from 'react-bootstrap';
import useCustomTemplateToolbarSettings from './useCustomTemplateToolbarSettings';
import { CustomTemplateAddEditFormValues } from './CustomTemplateAddEditModal.schema';
import {
  AutoTranslatedRowStyled,
  InfoRowStyled,
  StyledCheckboxField,
} from './CustomTemplateAddEditModal.styles';

const CustomTemplateAddEditModalFormFields = ({
  availableLanguages,
  onChangeAutoTranslate,
  isEditMode,
}: {
  availableLanguages: LanguageCode[];
  onChangeAutoTranslate: (value: boolean) => void;
  isEditMode?: boolean;
}) => {
  const { t } = useTranslation();
  const { setValue, trigger } =
    useFormContext<CustomTemplateAddEditFormValues>();
  const messagingTypeValue = useWatch({ name: 'messagingType' });
  const languageValue = useWatch({ name: 'language' });
  const autoTranslateValue = useWatch({ name: 'autoTranslate' });
  const [isOnPreviewMode, setIsOnPreviewMode] = useState(false);
  const {
    data: previewData,
    mutateAsync: fetchPreview,
    isLoading: isLoadingPreview,
  } = useSystemTemplatePreviewMutation();

  const shouldShowSubject = messagingTypeValue === MessagingType.EMAIL;

  const { toolbarSettings, isLoadingVariables } =
    useCustomTemplateToolbarSettings({
      loadPreview: async (content) => {
        const { data } = await fetchPreview({ htmlContent: content });

        return data.previewContent;
      },
      messagingType: messagingTypeValue,
      onEnterPreviewMode: () => setIsOnPreviewMode(true),
      onExitPreviewMode: () => setIsOnPreviewMode(false),
    });

  const isNonEnglishLanguageSelected = languageValue !== LanguageCode.EN;

  const handleChangeAutoTranslate = (event: React.FormEvent<FormControl>) => {
    const isChecked = (event?.target as HTMLInputElement)?.checked;
    if (isChecked) {
      setValue('language', LanguageCode.EN);
    }
    onChangeAutoTranslate(isChecked);
    trigger();
  };

  const isDisabledLanguageSelect =
    availableLanguages.length <= 1 || (!!autoTranslateValue && isEditMode);

  return (
    <div>
      <SelectField
        name="messagingType"
        colSmInput={9}
        colSmLabel={3}
        label={t('pageAutomations.customTemplates.modal.templateType')}
        options={Object.values(MessagingType).map((type) => ({
          label: t('pageAutomations.customTemplates.types.messagingType', {
            context: type,
          }),
          value: type,
        }))}
        disabled
        required
      />

      <TextField
        required
        name="name"
        colSmInput={9}
        colSmLabel={3}
        label={t('pageAutomations.customTemplates.modal.templateName')}
        disabled={isNonEnglishLanguageSelected}
      />
      {isNonEnglishLanguageSelected && (
        <InfoRowStyled>
          <Col sm={9} smOffset={3} xs={8} xsOffset={4}>
            {t('pageAutomations.customTemplates.modal.templateNameInfo')}
          </Col>
        </InfoRowStyled>
      )}

      <hr />

      <TemplateModalLanguageSelectField
        usageType={UsageType.CUSTOM}
        isDisabled={isDisabledLanguageSelect}
      />
      {autoTranslateValue && (
        <InfoRowStyled>
          <Col sm={9} smOffset={3} xs={8} xsOffset={4}>
            {t('pageAutomations.customTemplates.modal.autoTranslateInfo')}
          </Col>
        </InfoRowStyled>
      )}

      <AutoTranslatedRowStyled>
        <Col sm={3} xs={4} className="control-label">
          {t('pageAutomations.customTemplates.modal.autoTranslate')}
        </Col>
        <StyledCheckboxField
          name="autoTranslate"
          onChangeHandler={handleChangeAutoTranslate}
          className="col-sm-9 col-xs-8"
          label={t('pageAutomations.customTemplates.modal.autoTranslate')}
        >
          {t('pageAutomations.customTemplates.modal.autoTranslateAll')}
        </StyledCheckboxField>
      </AutoTranslatedRowStyled>
      <hr />
      {shouldShowSubject && isOnPreviewMode && (
        <TextField
          colSmInput={9}
          colSmLabel={3}
          disabled
          isLoading={isLoadingPreview}
          key={`subject-${languageValue}`}
          label={t('pageAutomations.customTemplates.modal.subject')}
          name="subjectPreview"
          required
          value={
            previewData?.data.previewTitle ??
            t('form.editor.preview.errorMessage')
          }
        />
      )}
      {availableLanguages.map((language) => {
        if (!language) {
          return null;
        }

        const isNonEnglishWithAutoTranslate =
          autoTranslateValue && language !== LanguageCode.EN;

        return (
          <LanguageFieldsContainer
            key={language}
            $isVisible={languageValue === language}
          >
            {shouldShowSubject && !isOnPreviewMode && (
              <TextField
                required
                name={`locales.${language}.subject`}
                colSmInput={9}
                colSmLabel={3}
                label={t('pageAutomations.customTemplates.modal.subject')}
                disabled={isNonEnglishWithAutoTranslate}
                placeholder={`${
                  isNonEnglishWithAutoTranslate
                    ? t(
                        'pageAutomations.customTemplates.modal.subjectAutoTranslatePlaceholder',
                      )
                    : ''
                }`}
              />
            )}

            <RichTextEditorField
              colSmInput={12}
              linkMode="edit"
              name={`locales.${language}.content`}
              toolbarSettings={toolbarSettings}
              isLoading={isLoadingVariables}
              readOnly={isNonEnglishWithAutoTranslate}
            />
          </LanguageFieldsContainer>
        );
      })}

      <InvalidLanguageAlert i18nKey="pageAutomations.customTemplates.modal.invalidLocaleError" />
    </div>
  );
};

CustomTemplateAddEditModalFormFields.defaultProps = {
  isEditMode: false,
};

export default CustomTemplateAddEditModalFormFields;
